import { useMutation } from '@apollo/client';
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import InputField from 'components/InputField/InputField';

import {
  Channel,
  ChannelConfig,
  RoutePrioritiesArrayItem,
  RoutePrioritiesEnum,
  SettingTypes,
} from "TypesAndInterfaces";
import InputKeyField from 'components/InputKeyField/InputKeyFiels';
import Button from 'components/Button/Button';
import { displayError, displaySuccess, displayWarning } from 'utils/displayNotification';
import { deleteMarketplaceChannelMutation, startFullSyncForSpecificChannelMutation } from 'pages/FirstMarketplace/mutations';
import { useEffect, useState } from 'react';
import ToggleButton from 'components/ToggleButton/ToggleButton';
import DragAndDropDropdown from 'components/Dropdown/DragAndDropDropdown';

interface IChannelError {
  channelId: number;
  errors: [
    {
      message: string;
      [key: string]: string;
    },
  ];
}

const routePriorities: RoutePrioritiesArrayItem[] = [
  {
    value: RoutePrioritiesEnum.regular,
    label: RoutePrioritiesEnum.regular,
    key: 0
  },
  {
    value: RoutePrioritiesEnum.fast,
    label: RoutePrioritiesEnum.fast,
    key: 1
  },
  {
    value: RoutePrioritiesEnum.wire,
    label: RoutePrioritiesEnum.wire,
    key: 2
  },
  {
    value: RoutePrioritiesEnum.instant,
    label: RoutePrioritiesEnum.instant,
    key: 3
  },
  {
    value: RoutePrioritiesEnum.crossBorder,
    label: RoutePrioritiesEnum.crossBorder,
    key: 4
  },
  {
    value: RoutePrioritiesEnum.internal,
    label: RoutePrioritiesEnum.internal,
    key: 5
  }];

interface IProps {
  handleStateUpdate: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>, number?: boolean, type?: SettingTypes, channelID?: number, toogleProperty?: string, toogleState?: boolean, routePrioritiesName?: string, routePriorities?: string[]) => void;
  useChannels: boolean;
  channel: ChannelConfig;
  channelErrors: IChannelError[];
  channels: ChannelConfig[];
  marketplaceId?: number;
  handleToggleChange: (channelId: number | undefined, option: string) => void;
  setSaveButtonDisabled: Function;
  usePreBalancePlatforms: boolean;
  isPreBalanceFlagEnabled: boolean;
}

const ChannelSettings = ({
  handleStateUpdate,
  useChannels,
  channel,
  channelErrors,
  channels,
  marketplaceId,
  handleToggleChange,
  setSaveButtonDisabled,
  usePreBalancePlatforms,
  isPreBalanceFlagEnabled
}: IProps) => {
  const [syncChannelLoading, setSyncChannelLoading] = useState(false);
  const [allowBankAccountFormatSelection, setAllowBankAccountFormatSelection] = useState<boolean>(channel.config?.hopOptions?.allowBankAccountFormatSelection ?? false);
  const [allowIntraRegionCrossBorderPayout, setAllowIntraRegionCrossBorderPayout] = useState<boolean>(channel.config?.hopOptions?.allowIntraRegionCrossBorderPayout ?? false);
  const [routePrioritie, setRoutePrioritie] = useState<RoutePrioritiesArrayItem[]>([{
    value: RoutePrioritiesEnum.regular,
    label: RoutePrioritiesEnum.regular,
    key: 0
  }]);

  const [deleteChannel] = useMutation<
    { deleteChannel: Channel },
    { channelId: number }
  >(deleteMarketplaceChannelMutation, {
    onCompleted: () => {
      window.location.reload();
      displaySuccess("Channel deleted");
    },
    onError: (e) => {
      displayError(e.message);
      setTimeout(() => {
      }, 1500);
    }
  });

  useEffect(() => {
    if (channel.config?.hopOptions?.allowBankAccountFormatSelection !== undefined) {
      setAllowBankAccountFormatSelection(channel.config.hopOptions?.allowBankAccountFormatSelection)
    }
    if (channel.config?.hopOptions?.allowIntraRegionCrossBorderPayout !== undefined) {
      setAllowIntraRegionCrossBorderPayout(channel.config.hopOptions?.allowIntraRegionCrossBorderPayout)
    }
    if (channel.config?.routePriorities && channel.config.routePriorities.length > 0) {
      const filteredPriorities = routePriorities.filter(priority =>
        channel.config.routePriorities?.includes(priority.value)
      );
      filteredPriorities.sort((a, b) => {
        if (channel.config.routePriorities) {
          return channel.config.routePriorities.indexOf(a.value) - channel.config.routePriorities.indexOf(b.value);
        }
        return 0;
      }
      );
      setRoutePrioritie(filteredPriorities);
    }
  }, [channel.config])

  const [startFullSyncForSpecificChannel] = useMutation<
    { startFullSyncForSpecificChannel: boolean },
    { marketplaceId: number, channelId: number }
  >(startFullSyncForSpecificChannelMutation, {
    onCompleted: () => {
      setSyncChannelLoading(false);
      displaySuccess(`Channel synchronized successfully.`);
    },
    onError: (e) => {
      setSyncChannelLoading(false);
      displayError(e.message);
    }
  });

  const validateChannelField = (channelId: number, fieldName: string) => {
    const error = channelErrors.find((error) => error.channelId === channelId);
    if (error && error.errors.find((error) => error.field === fieldName)) {
      return false;
    }
    return true;
  };

  const isDefaultChannel = (channelId: number) => {
    const channel = channels.find((channel) => channel.id === channelId);
    if (channel && channel.isDefault) {
      return true;
    }
    return false;
  };

  const generateFieldErrors = (channelId: number, fieldName: string) => {
    const errors = [];
    if (!validateChannelField(channelId, fieldName)) {
      for (let c of channelErrors) {
        const error = c.errors.find((e) => e.field === fieldName);
        if (c.channelId === channelId && error) {
          errors.push(error.message);
        }
      }
    }
    return errors;
  };

  const handleSyncChannel = async (channelId: number) => {
    try {
      console.log(`Attempting to sync channel with id: ${channelId}`);
      // const channelToSync = channels.find(channel => channel.id === channelId);

      if (marketplaceId) {
        setSyncChannelLoading(true);
        await startFullSyncForSpecificChannel({ variables: { marketplaceId, channelId } });
      } else {
        displayError(`\nThe marketplaceId is missing.\nSave the marketplace first and try again.`);
      }
    } catch (error: any) {
      console.error(`Error syncing channel ${channelId}:`, error);
      displayError(`Error syncing channel ${channelId}: ${error.message}`);
    }
  }

  const handleDeleteChannel = async (channelId: number) => {
    if (isDefaultChannel(channelId)) {
      displayError(`\nThe channel cannot be remove because is a default channel. Please, contact to the support`);
    } else {
      await deleteChannel({ variables: { channelId } });
    }
  }

  const prepopulateLEMApiURL = () => {
    let url = ""
    if (process.env.REACT_APP_BACKEND_SERVER && (process.env.REACT_APP_BACKEND_SERVER.includes("localhost") || process.env.REACT_APP_BACKEND_SERVER.includes("dev") || process.env.REACT_APP_BACKEND_SERVER.includes("stg"))) {
      url = "https://kyc-test.adyen.com"
    } else {
      url = "https://kyc-live.adyen.com"
    }
    return url
  }

  const prepopulateBalanceApiURL = () => {
    let url = ""
    if (process.env.REACT_APP_BACKEND_SERVER && (process.env.REACT_APP_BACKEND_SERVER.includes("localhost") || process.env.REACT_APP_BACKEND_SERVER.includes("dev") || process.env.REACT_APP_BACKEND_SERVER.includes("stg"))) {
      url = "https://balanceplatform-api-test.adyen.com"
    } else {
      url = "https://balanceplatform-api-live.adyen.com"
    }
    return url
  }

  useEffect(() => {
    if (routePrioritie.length === 0) {
      setSaveButtonDisabled(true);
      displayWarning(`Channel payout priority is required!`);
    } else {
      setSaveButtonDisabled(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePrioritie]);

  return <>
    <div className="inputs-wrapper__inputs">
      <p className="main-content__sidebar__title">{channel.code} Settings</p>
      {useChannels && (
        <InputField
          label="Channel Name"
          placeholder="Enter Channel Name"
          name="code"
          onChange={(e) => handleStateUpdate(e, false, useChannels  ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
          value={channel.code}
          style={!validateChannelField(channel.id!, 'code') ? { border: '2px solid red' } : {}}
          errors={generateFieldErrors(channel.id!, 'code')}
        />
      )}
      <InputField
        label="Balance Platform Name"
        placeholder="Enter Balance Platform Name"
        name="balancePlatform"
        onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
        value={channel.balancePlatform}
      />
      {usePreBalancePlatforms && isPreBalanceFlagEnabled && (
        <>
          <InputField
            label="Pre-Balance Account ID *"
            placeholder="Enter Pre-Balance Account ID"
            name="preBalanceAccountId"
            onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
            value={channel.preBalanceAccountId}
          />
          <InputField
            label="Pre-Balance Platform Name"
            placeholder="Enter Pre-Balance Platform Name"
            name="preBalancePlatformName"
            onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
            value={channel.preBalancePlatformName}
          />
        </>
      )}
      <InputField
        label="Balance Account Id"
        placeholder="Enter Balance Account Id"
        name="balanceAccountId"
        onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
        value={channel.balanceAccountId}
      />

      <DragAndDropDropdown
        options={routePriorities}
        label="Payout priorities"
        placeholder="Enter Payout priorities"
        onChange={(e: any) => {
          const valuesArray: string[] = e.map((item: any) => item.value);
          setRoutePrioritie(e);
          handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id, undefined, undefined, "config.routePriorities", valuesArray)
        }}
        value={routePrioritie}
        isMulti
        className='dropdown-payout'
      />
      <div className="radio-validate-container">
        <div className="radio-container">
          <section>
            <div>
              <p>Allow Bank Account Format Selection</p>
              <ToggleButton
                onChange={(e) => {
                  handleToggleChange(channel.id, "allowBankAccountFormatSelection")
                  handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id, "config.hopOptions.allowBankAccountFormatSelection", !allowBankAccountFormatSelection)
                  setAllowBankAccountFormatSelection(!allowBankAccountFormatSelection)
                }}
                name="allowBankAccountFormatSelection"
                checked={allowBankAccountFormatSelection}
                checkedText="enabled"
                uncheckedText="disabled"
              />
            </div>
          </section>
          <section>
            <div>
              <p>Allow Intra-Region Cross-Border Payout</p>
              <ToggleButton
                onChange={(e) => {
                  handleToggleChange(channel.id, "allowIntraRegionCrossBorderPayout")
                  handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id, "config.hopOptions.allowIntraRegionCrossBorderPayout", !allowIntraRegionCrossBorderPayout)
                  setAllowIntraRegionCrossBorderPayout(!allowIntraRegionCrossBorderPayout)
                }}
                name="allowIntraRegionCrossBorderPayout"
                checked={allowIntraRegionCrossBorderPayout}
                checkedText="enabled"
                uncheckedText="disabled"
              />
            </div>
          </section>
        </div>
      </div>
    </div>
    <section id="credentials">
      <span id="lemApi">
        <h3>Lem Api</h3>
        <InputField
          label="URL"
          placeholder="Enter URL"
          name="lemApi.url"
          onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
          value={channel.lemApi?.url ? channel.lemApi?.url : prepopulateLEMApiURL()}
        />
        <InputKeyField
          label="Key"
          placeholder="Enter Key"
          name="lemApi.key"
          onChange={(e) => {
            handleStateUpdate(
              e,
              false,
              useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
              channel.id
            )
          }
          }
          ofValue={channel.lemApi?.key}
        />
        <InputField
          label="Username"
          placeholder="Enter Username"
          name="lemApi.user"
          onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
          value={channel.lemApi?.user}
          pattern="[^:]*"
        />
        <InputKeyField
          label="Password"
          placeholder="Enter Password"
          name="lemApi.password"
          onChange={(e) =>
            handleStateUpdate(
              e,
              false,
              useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
              channel.id
            )
          }
          ofValue={channel.lemApi?.password}
        />
      </span>
      <span id="balanceApi">
        <h3>Balance Api</h3>
        <InputField
          label="URL"
          placeholder="Enter URL"
          name="balanceApi.url"
          onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
          value={channel.balanceApi?.url ? channel.balanceApi?.url : prepopulateBalanceApiURL()}
        />
        <InputKeyField
          label="Key"
          placeholder="Enter Key"
          name="balanceApi.key"
          onChange={(e) =>
            handleStateUpdate(
              e,
              false,
              useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
              channel.id
            )
          }
          ofValue={channel.balanceApi?.key}
        />
        <InputField
          label="Username"
          placeholder="Enter Username"
          name="balanceApi.user"
          onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
          value={channel.balanceApi?.user}
          pattern="[^:]*"
        />
        <InputKeyField
          label="Password"
          placeholder="Enter Password"
          name="balanceApi.password"
          onChange={(e) =>
            handleStateUpdate(
              e,
              false,
              useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
              channel.id
            )
          }
          ofValue={channel.balanceApi?.password}
        />
      </span>
    </section>
    <section id="notifications">
      <h3>Notification Settings</h3>
      <span className="notificationUrl">
        <article>
          <h4>Notification URL: </h4>
          {channel.notificationUrl}
        </article>
      </span>
      <Tabs forceRenderTabPanel>
        <TabList>
          <Tab>Configuration</Tab>
          <Tab>Transfer</Tab>
          <Tab>Transfer Instrument</Tab>
          <Tab>Legal Entity</Tab>
        </TabList>
        <TabPanel>
          <InputField
            label="Username"
            placeholder="Enter Username"
            name="notificationSettings.configuration.user"
            onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
            value={channel.notificationSettings?.configuration?.user}
            pattern="[^:]*"
          />
          <InputKeyField
            label="Password"
            placeholder="Enter Password"
            name="notificationSettings.configuration.password"
            onChange={(e) =>
              handleStateUpdate(
                e,
                false,
                useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
                channel.id
              )
            }
            ofValue={
              channel.notificationSettings?.configuration?.password
            }
          />
          <InputKeyField
            label="HMac"
            placeholder="Enter HMac"
            name="notificationSettings.configuration.hmac"
            onChange={(e) =>
              handleStateUpdate(
                e,
                false,
                useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
                channel.id
              )
            }
            ofValue={channel.notificationSettings?.configuration?.hmac}
          />
        </TabPanel>
        <TabPanel>
          <InputField
            label="Username"
            placeholder="Enter Username"
            name="notificationSettings.transfer.user"
            onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
            value={channel.notificationSettings?.transfer?.user}
            pattern="[^:]*"
          />
          <InputKeyField
            label="Password"
            placeholder="Enter Password"
            name="notificationSettings.transfer.password"
            onChange={(e) =>
              handleStateUpdate(
                e,
                false,
                useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
                channel.id
              )
            }
            ofValue={channel.notificationSettings?.transfer?.password}
          />
          <InputKeyField
            label="HMac"
            placeholder="Enter HMac"
            name="notificationSettings.transfer.hmac"
            onChange={(e) =>
              handleStateUpdate(
                e,
                false,
                useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
                channel.id
              )
            }
            ofValue={channel.notificationSettings?.transfer?.hmac}
          />
        </TabPanel>
        <TabPanel>
          <InputField
            label="Username"
            placeholder="Enter Username"
            name="notificationSettings.transferInstrument.user"
            onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
            value={channel.notificationSettings?.transferInstrument?.user}
            pattern="[^:]*"
          />
          <InputKeyField
            label="Password"
            placeholder="Enter Password"
            name="notificationSettings.transferInstrument.password"
            onChange={(e) =>
              handleStateUpdate(
                e,
                false,
                useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
                channel.id
              )
            }
            ofValue={
              channel.notificationSettings?.transferInstrument?.password
            }
          />
          <InputKeyField
            label="HMac"
            placeholder="Enter HMac"
            name="notificationSettings.transferInstrument.hmac"
            onChange={(e) =>
              handleStateUpdate(
                e,
                false,
                useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
                channel.id
              )
            }
            ofValue={
              channel.notificationSettings?.transferInstrument?.hmac
            }
          />
        </TabPanel>
        <TabPanel>
          <InputField
            label="Username"
            placeholder="Enter Username"
            name="notificationSettings.legalEntity.user"
            onChange={(e) => handleStateUpdate(e, false, useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform, channel.id)}
            value={channel.notificationSettings?.legalEntity?.user}
            pattern="[^:]*"
          />
          <InputKeyField
            label="Password"
            placeholder="Enter Password"
            name="notificationSettings.legalEntity.password"
            onChange={(e) =>
              handleStateUpdate(
                e,
                false,
                useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
                channel.id
              )
            }
            ofValue={channel.notificationSettings?.legalEntity?.password}
          />
          <InputKeyField
            label="HMac"
            placeholder="Enter HMac"
            name="notificationSettings.legalEntity.hmac"
            onChange={(e) =>
              handleStateUpdate(
                e,
                false,
                useChannels ? SettingTypes.Channel : SettingTypes.BalancePlatform,
                channel.id
              )
            }
            ofValue={channel.notificationSettings?.legalEntity?.hmac}
          />
        </TabPanel>
      </Tabs>
    </section>
    <section id="management">
      <Button
        className="button--bordered button--green"
        type="button"
        content={useChannels ? "Sync Channel" : "Sync BalancePlatform"}
        icon="sync"
        disabled={syncChannelLoading || !channel || !channel.id}
        onClick={() => {
          if (channel && channel.id) {
            handleSyncChannel(channel.id);
          }
        }}
      />
      <Button
        className="button--bordered button--error"
        type="button"
        content={useChannels ? "Delete Channel" : "Delete Balance Platform"}
        icon=""
        disabled={!channel || !channel.id}
        onClick={() => {
          if (channel && channel.id) {
            handleDeleteChannel(channel.id)
          }
        }}
      />
    </section>
  </>
};

export default ChannelSettings;
